import * as React from 'react';
import { useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import isEmpty from 'lodash/isEmpty';

import {
  Alert,
  Button,
  ButtonGroup,
  DataGridPremium,
  DetailPanelExpansionType,
  Divider,
  GridColDef,
  GridRowParams,
  GridValidRowModel,
  StateAwareGridPremium,
  styled,
  useGridApiRef,
} from '@sbv-clients/ui-shared';
import { nameOfFactory } from '@sbv-clients/util-misc';

import { ParentMenuWithSubMenuItemsType } from '../../utils/transform';
import DetailsTable from './details-table';
import { ActionsType } from './use-actions';

const menuKeys = nameOfFactory<ParentMenuWithSubMenuItemsType>();
interface FacilityMenuDataGridProps {
  data: ParentMenuWithSubMenuItemsType[];
  isLoading: boolean;
  actions: ActionsType;
  detailPanelExpansion: DetailPanelExpansionType;
}

const FacilityMenuDataGrid = ({
  data,
  isLoading,
  actions,
  detailPanelExpansion,
}: FacilityMenuDataGridProps) => {
  const apiRef = useGridApiRef();

  const columns = useMemo(() => {
    return [
      {
        field: menuKeys('order'),
        headerName: 'Order',
        width: 100,
        sortable: false,
      },
      {
        field: menuKeys('label'),
        headerName: 'Label',
        width: 200,
        sortable: false,
      },
      {
        field: menuKeys('link'),
        headerName: 'Link',
        width: 200,
        sortable: false,
      },
      {
        field: 'actions',
        headerName: '',
        width: 320,
        maxWidth: 500,
        sortable: false,
        filterable: false,
        align: 'right',
        renderCell: (params) => {
          const hasChild = params?.row?.items?.length > 0;
          const id = params?.row?.id as string;
          return (
            <Box>
              <ButtonGroup>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => actions.onAddParentMenuItem(id)}
                >
                  Add Child Menu
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => actions.onEditMenuItem(params.row)}
                >
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={hasChild}
                  onClick={() => actions.onDeleteMenuItem(id)}
                >
                  Delete
                </Button>
              </ButtonGroup>
            </Box>
          );
        },
      },
    ] as GridColDef[];
  }, [actions]);

  const getDetailPanelContent = useCallback(
    (params: GridRowParams<GridValidRowModel>) => {
      const row = params.row as ParentMenuWithSubMenuItemsType;
      return (
        <>
          <Box p={4} bgcolor="white">
            {isEmpty(row.children) ? (
              <Alert severity="info">No item found</Alert>
            ) : (
              <DetailsTable data={row} actions={actions} />
            )}
          </Box>
          <Divider />
        </>
      );
    },
    [actions]
  );

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <StateAwareGridPremium
        name="base-facility-menu-data-grid"
        apiRef={apiRef}
        rows={data}
        columns={columns}
        disableColumnMenu
        loading={isLoading}
        hideFooter
        getDetailPanelHeight={() => 'auto'}
        getDetailPanelContent={getDetailPanelContent}
        detailPanelExpandedRowIds={
          detailPanelExpansion.detailPanelExpandedRowIds
        }
        onDetailPanelExpandedRowIdsChange={
          detailPanelExpansion.onDetailPanelExpandedRowIdsChange
        }
      />
    </Box>
  );
};

export default FacilityMenuDataGrid;
